/**
 * Players' names in Tibbs's voice (docs/TIBBS-VOICE.md 5.4). Computer players'
 * names ship in the voice pack; anyone else's is rendered once by the Worker
 * (GET /api/voice/name/<name>.mp3, the Cartesia key stays on the server) and
 * kept in Cache Storage by the page itself, so a name heard once works offline.
 */
import type { NameForm } from './plan';

export const NAME_CACHE = 'ancientnine-voice-names-v1';
const FETCH_TIMEOUT_MS = 5000;

export function nameClipUrl(name: string, form: NameForm): string {
  return `/api/voice/name/${encodeURIComponent(name.trim().replace(/\s+/g, ' '))}.mp3?form=${form}`;
}

/** Same rule as the Worker: 1 to 24 letters, spaces, apostrophes and hyphens. */
export function speakableName(name: string): boolean {
  const n = name.normalize('NFC').trim().replace(/\s+/g, ' ');
  return n.length >= 1 && n.length <= 24 && /^[\p{L}\p{M}' ’-]+$/u.test(n) && /\p{L}/u.test(n);
}

async function openCache(): Promise<Cache | null> {
  try {
    return typeof caches === 'undefined' ? null : await caches.open(NAME_CACHE);
  } catch {
    return null;
  }
}

/** Names known to be cached on this device (filled as we look), so plans can be built synchronously. */
const known = new Set<string>();
const inflight = new Map<string, Promise<boolean>>();

export function nameKnown(url: string): boolean {
  return known.has(url);
}

/** Is the clip on this device already? */
export async function nameCached(url: string): Promise<boolean> {
  if (known.has(url)) return true;
  const c = await openCache();
  const hit = c ? await c.match(url).catch(() => undefined) : undefined;
  if (hit) known.add(url);
  return !!hit;
}

/**
 * Make sure a name clip is on this device: from the cache, else from the
 * Worker (stored for next time). Resolves false when it cannot be had (offline,
 * no key on the server, rate limited, a name Tibbs cannot say).
 */
export function ensureName(name: string, form: NameForm): Promise<boolean> {
  if (!speakableName(name)) return Promise.resolve(false);
  const url = nameClipUrl(name, form);
  if (known.has(url)) return Promise.resolve(true);
  const running = inflight.get(url);
  if (running) return running;
  const p = (async () => {
    if (await nameCached(url)) return true;
    if (typeof fetch === 'undefined') return false;
    const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = setTimeout(() => ctl?.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(url, ctl ? { signal: ctl.signal } : {});
      if (!res.ok || !(res.headers.get('content-type') ?? '').includes('audio')) return false;
      const c = await openCache();
      if (c) await c.put(url, res.clone()).catch(() => {});
      // Keep the bytes in memory too, in case Cache Storage is unavailable (private mode).
      memory.set(url, await res.arrayBuffer());
      known.add(url);
      return true;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
      inflight.delete(url);
    }
  })();
  inflight.set(url, p);
  return p;
}

const memory = new Map<string, ArrayBuffer>();

/** The clip's bytes, from memory or the cache (never the network). */
export async function nameBytes(url: string): Promise<ArrayBuffer | null> {
  const m = memory.get(url);
  if (m) return m.slice(0);
  const c = await openCache();
  const hit = c ? await c.match(url).catch(() => undefined) : undefined;
  return hit ? hit.arrayBuffer() : null;
}

/** Fetch both forms of every name in the background (game start, a saved name). */
export function prefetchNames(names: readonly string[]): void {
  for (const n of names) for (const f of ['plain', 'proclaim'] as const) void ensureName(n, f);
}
